/**
 * What counts as "new" on the Inbox tabs.
 *
 * Chat: the server's own unread count (/chat/unread/count), cleared when the parent opens
 * the conversation (ChatScreen marks it read).
 *
 * Events: the server has no read state for an event, so the app remembers, per parent,
 * which events it has shown them; any other event is new until the Events tab is opened.
 * The first time there is nothing remembered, today's events are taken as seen, so a
 * parent is not greeted with every event the school ever posted.
 */

export const seenEventsKey = (ownerId: string) => `@inbox_seen_events:${ownerId}`;

export interface UnseenEvents {
  count: number;
  /** The seen list to save: the current ids on the first run, otherwise unchanged. */
  seen: string[];
}

export function unseenEvents(currentIds: string[], seenIds: string[] | null): UnseenEvents {
  const ids = [...new Set(currentIds.filter(Boolean))];
  if (seenIds === null) return { count: 0, seen: ids };
  const seen = new Set(seenIds);
  return { count: ids.filter(id => !seen.has(id)).length, seen: seenIds };
}

/** The ids of the events in a GET /event answer, whichever shape it came in. */
export function eventIdsFrom(data: any): string[] {
  const list: any[] = data?.Events ?? data?.events ?? (Array.isArray(data) ? data : []);
  return list.map(e => String(e?.id ?? e?.Id ?? e?.eventId ?? e?.EventId ?? '')).filter(Boolean);
}

/** "Chat (2)" - the label a tab shows with its count, for screen readers too. */
export const tabLabelWithCount = (tab: string, count: number) => (count > 0 ? `${tab} (${count})` : tab);

/** Badge text: the count, capped so it fits a small circle. */
export const badgeText = (count: number) => (count > 99 ? '99+' : String(count));
